/* ==========================================================================
   LPL — Find Their System · V2 "The Counter"

   Progressive enhancement only. Everything — the question, the four choices
   with their recognition copy, all three systems, every dose, the email form
   and the PDP links — is server-rendered by sections/lpl-finder.liquid. The
   inline gate in that section adds .is-live before paint; this file adds
   behaviour and ONLY EVER SUBTRACTS.

   One card. The question pane hides, the answer pane shows, and the frame
   itself never moves. Both panes are server-rendered, so with JS blocked the
   page is a working form plus a readable record of all three systems.

   Nothing here fetches, awaits or promises between a tap and the answer.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-lplf-root]');
  if (!root) return;

  var STORE = 'lpl.finder.v3';
  var TIER_FOR_BAND = { under7: 'lite', '7to30': 'standard', over30: 'double' };

  var form = root.querySelector('[data-lplf-form]');
  var boxes = [].slice.call(root.querySelectorAll('[data-lplf-choice]'));
  var cta = root.querySelector('[data-lplf-submit]');
  var helper = root.querySelector('[data-lplf-helper]');
  var stack = root.querySelector('[data-lplf-result]');
  var turn = root.querySelector('[data-lplf-turn]');
  var systems = [].slice.call(root.querySelectorAll('[data-lplf-match-block]'));
  var restart = [].slice.call(root.querySelectorAll('[data-lplf-restart]'));
  var weights = [].slice.call(root.querySelectorAll('[data-lplf-weight]'));

  if (!form || !boxes.length || !cta || !stack) return;

  var data = {};
  try {
    data = JSON.parse(root.querySelector('[data-lplf-data]').textContent);
  } catch (e) { data = {}; }
  var T = data.turn || { lead: 'You mentioned ', join: ', ', last: ' and ', end: '.' };
  var OFFER_CODE = String((data.offer && data.offer.code) || 'WELCOME10').trim();

  var asked = false;

  /* ---------------- analytics ---------------------------------------------
     Shopify.analytics.publish only. klaviyo.push(['track'|'identify']) is
     deliberately absent: measured on this store 2026-08-13, Klaviyo's onsite
     endpoint a.klaviyo.com/onsite/track-analytics is CORS-rejected, so those
     calls deliver nothing and only add console noise. Verified pre-existing
     and store-wide — the existing footer newsletter form on the LIVE theme
     raises the identical errors. The six finder_* values still reach the
     Klaviyo profile as `properties` on the subscribe request below. */
  function publish(name, props) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, props || {});
      }
    } catch (e) { /* analytics must never break the finder */ }
  }

  /* ---------------- state readers ----------------------------------------- */
  function checked() { return boxes.filter(function (b) { return b.checked; }); }
  function concerns() { return checked().map(function (b) { return b.value; }); }
  function fragments() {
    return checked().map(function (b) { return b.getAttribute('data-fragment') || b.value; });
  }
  function match() { return root.dataset.match || null; }
  function band() { return root.dataset.weight || null; }
  function tier() { return band() ? TIER_FOR_BAND[band()] : null; }

  function activeSystem() {
    var key = match();
    if (!key) return null;
    for (var i = 0; i < systems.length; i++) {
      if (systems[i].dataset.lplfMatchBlock === key) return systems[i];
    }
    return null;
  }

  function snapshot() {
    return {
      finder_match: match(),
      finder_concerns: concerns().join(','),
      finder_weight_band: band(),
      finder_routine: tier(),
      finder_source: data.source || 'find-their-system',
      finder_offer_code: OFFER_CODE,
      finder_completed_at: new Date().toISOString()
    };
  }

  /* ---------------- persistence ------------------------------------------- */
  function save() {
    try {
      sessionStorage.setItem(STORE, JSON.stringify({
        concerns: concerns(),
        match: match(),
        weight: band(),
        sent: root.dataset.sent === 'true'
      }));
    } catch (e) { /* private mode: the finder still works, just not on return */ }
  }

  function stamp() {
    // Non-personal state only. An email address never touches the URL or storage.
    if (!window.history || !history.replaceState) return;
    try {
      var u = new URL(window.location.href);
      if (match()) u.searchParams.set('m', match()); else u.searchParams.delete('m');
      if (band()) u.searchParams.set('w', band()); else u.searchParams.delete('w');
      history.replaceState(history.state, '', u.toString());
    } catch (e) { /* ignore */ }
  }

  /* ---------------- the reader's own words, given back -------------------- */
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function turnSentence(frags) {
    var b = function (t) { return '<b>' + escapeHtml(t) + '</b>'; };
    if (!frags.length) return '';
    // Shopify trims text settings. Own sentence spacing here, not in settings.
    var lead = String(T.lead || '').trim();
    var join = String(T.join || ',').trim();
    var last = String(T.last || 'and').trim();
    var prefix = lead ? escapeHtml(lead) + ' ' : '';
    if (frags.length === 1) return prefix + b(frags[0]) + escapeHtml(T.end);
    if (frags.length === 2) {
      return prefix + b(frags[0]) + ' ' + escapeHtml(last) + ' ' + b(frags[1]) + escapeHtml(T.end);
    }
    // Oxford comma: "a, b, and c".
    var head = frags.slice(0, -1).map(b).join(escapeHtml(join) + ' ');
    return prefix + head + escapeHtml(join) + ' ' + escapeHtml(last) + ' ' +
      b(frags[frags.length - 1]) + escapeHtml(T.end);
  }

  /* ---------------- the CTA and its helper line --------------------------- */
  function syncCta() {
    var armed = checked().length > 0;
    cta.setAttribute('aria-disabled', String(!armed));
    if (helper) {
      helper.textContent = armed
        ? (helper.getAttribute('data-armed') || '')
        : (helper.getAttribute('data-unarmed') || '');
    }
  }

  /* ---------------- shop hrefs -------------------------------------------- */
  function discountUrl(code, destination) {
    var normalized = String(code || '').trim();
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(normalized)) return destination;
    try {
      var url = new URL(destination || '/', window.location.origin);
      if (url.origin !== window.location.origin) return destination;
      var redirect = url.pathname + url.search + url.hash;
      return '/discount/' + encodeURIComponent(normalized) + '?redirect=' + encodeURIComponent(redirect);
    } catch (e) { return destination; }
  }

  function syncShop() {
    systems.forEach(function (article) {
      var a = article.querySelector('[data-lplf-shop]');
      if (!a) return;
      var base = article.dataset.lplfShopBase;
      if (!base) return;
      var t = tier();
      var id = t ? article.getAttribute('data-lplf-variant-' + t) : null;
      // ?variant= pins tier AND supply for the no-JS path; ?routine= is what the
      // PDP element reads at init. Neither preselects one-time vs subscription:
      // selling_plan is never written and data-default-mode is never touched.
      var target = id ? base + '?variant=' + id + '&routine=' + t : base;
      a.dataset.lplfTarget = target;
      a.href = root.dataset.sent === 'true' ? discountUrl(OFFER_CODE, target) : target;
    });
  }

  function syncEmailState() {
    var accepted = root.dataset.sent === 'true';
    systems.forEach(function (article) {
      var capture = article.querySelector('[data-lplf-email-capture]');
      var success = article.querySelector('[data-lplf-email-status]');
      if (capture) capture.hidden = accepted;
      if (success) success.hidden = !accepted;
    });
    syncShop();
  }

  /* ---------------- the narrowing ----------------------------------------- */
  function resolve(opts) {
    var options = opts || {};
    var picked = checked();
    if (!picked.length) return;

    var t = {};
    picked.forEach(function (b) { t[b.dataset.territory] = true; });
    var keys = Object.keys(t);
    var key = keys.length === 1 ? keys[0] : 'pampered';

    root.dataset.resolved = 'true';
    root.dataset.match = key;
    stack.dataset.lplfState = 'on';

    systems.forEach(function (article) {
      if (article.dataset.lplfMatchBlock === key) article.removeAttribute('hidden');
      else article.setAttribute('hidden', '');
    });

    var sentence = turnSentence(fragments());
    Array.prototype.forEach.call(root.querySelectorAll('[data-lplf-turn]'), function (el) {
      el.innerHTML = sentence;
    });

    var article = activeSystem();

    syncEmailState();
    save();
    stamp();

    if (!options.silent) {
      publish('finder_match_viewed', { finder_match: key, finder_concerns: concerns().join(',') });
      // The result card—not a heading halfway down it—is the scroll target.
      // Focus announces the match without moving the viewport a second time.
      var card = root.querySelector('.lplf-card');
      var h = article && article.querySelector('[data-lplf-name]');
      if (card && typeof card.scrollIntoView === 'function') {
        card.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' });
      }
      if (h) {
        try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); }
      }
    }
  }

  /* ---------------- weight ------------------------------------------------ */
  function applyBand(value, opts) {
    var options = opts || {};
    if (!TIER_FOR_BAND[value]) return;

    root.dataset.weight = value;
    weights.forEach(function (w) { w.checked = w.dataset.lplfWeight === value; });
    root.removeAttribute('data-need-weight');

    // An instruction, not a rate. Height is reserved, so this costs no layout.
    systems.forEach(function (article) {
      var chosen = article.querySelector('[data-lplf-weight="' + value + '"]');
      var dose = article.querySelector('[data-lplf-dose]');
      if (!chosen) return;
      if (dose) {
        dose.textContent = chosen.getAttribute('data-dose') || '';
        dose.removeAttribute('data-state');
      }
    });

    syncShop();
    save();
    stamp();

    if (!options.silent) {
      publish('finder_weight_selected', {
        finder_weight_band: value,
        finder_routine: TIER_FOR_BAND[value],
        finder_match: match()
      });
    }
  }

  function resetDose() {
    root.removeAttribute('data-weight');
    weights.forEach(function (w) { w.checked = false; });
    systems.forEach(function (article) {
      var dose = article.querySelector('[data-lplf-dose]');
      if (dose) {
        dose.textContent = dose.getAttribute('data-prompt') || '';
        dose.setAttribute('data-state', 'prompt');
      }
    });
  }

  /* ---------------- email -------------------------------------------------- */
  function legacyCopy(value) {
    var input = document.createElement('textarea');
    input.value = value;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    var copied = false;
    try { copied = document.execCommand('copy'); } catch (e) { copied = false; }
    input.remove();
    return copied;
  }

  function wireEmail(article) {
    var f = article.querySelector('[data-lplf-email-form]');
    if (!f) return;
    var input = f.querySelector('[data-lplf-email-input]');
    var submit = f.querySelector('[data-lplf-email-submit]');
    var error = f.querySelector('[data-lplf-email-error]');
    var shop = article.querySelector('[data-lplf-shop]');
    var copy = article.querySelector('[data-lplf-copy]');
    var code = article.querySelector('[data-lplf-code]');
    var copyStatus = article.querySelector('[data-lplf-copy-status]');

    function pending(on) {
      f.setAttribute('aria-busy', String(on));
      if (submit) {
        submit.disabled = on;
        submit.textContent = on
          ? (submit.getAttribute('data-busy') || 'Unlocking…')
          : (submit.getAttribute('data-idle') || 'Unlock $10 off');
      }
      if (on) root.dataset.lplfEmail = 'pending';
    }

    function fail() {
      pending(false);
      root.dataset.lplfEmail = 'error';
      if (error) error.hidden = false;
      publish('finder_email_signup_failed', snapshot());
      try { input.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
    }

    function accepted() {
      pending(false);
      root.dataset.sent = 'true';
      root.dataset.lplfEmail = 'accepted';
      if (error) error.hidden = true;
      syncEmailState();
      save();
      publish('finder_email_signup_accepted', snapshot());
      if (shop) {
        shop.textContent = shop.getAttribute('data-shop') || shop.textContent;
        try { shop.focus({ preventScroll: true }); } catch (e) { shop.focus(); }
      }
    }

    f.addEventListener('submit', function (event) {
      event.preventDefault();
      if (!input.checkValidity()) { input.reportValidity(); return; }
      var email = input.value.trim();
      if (!email) return;

      var props = snapshot();
      if (error) error.hidden = true;
      pending(true);
      publish('finder_email_submit_attempted', props);

      if (!(data.klaviyo && data.klaviyo.companyId && data.klaviyo.listId)) {
        fail();
        return;
      }

      var body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'LPL Finder | ' + (data.source || 'find-their-system'),
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: props,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: data.klaviyo.listId } } }
        }
      };

      try {
        var controller = window.AbortController ? new AbortController() : null;
        var timeout = window.setTimeout(function () {
          if (controller) controller.abort();
        }, 12000);
        fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(data.klaviyo.companyId), {
          method: 'POST',
          mode: 'cors',
          keepalive: true,
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            revision: data.klaviyo.revision || '2026-07-15'
          },
          signal: controller ? controller.signal : undefined,
          body: JSON.stringify(body)
        }).then(function (response) {
          window.clearTimeout(timeout);
          if (response.status !== 202) throw new Error('subscription_not_accepted');
          accepted();
        })['catch'](function () {
          window.clearTimeout(timeout);
          fail();
        });
      } catch (e) { fail(); }
    });

    if (copy && code) {
      copy.addEventListener('click', function () {
        var value = (code.textContent || OFFER_CODE).trim();
        var done = function (copied) {
          if (!copied) return;
          copy.textContent = copy.getAttribute('data-copied') || 'Copied';
          if (copyStatus) copyStatus.textContent = 'Copied to clipboard: ' + value;
          publish('finder_offer_code_copied', { finder_match: match(), finder_offer_code: value });
          window.setTimeout(function () {
            copy.textContent = copy.getAttribute('data-copy') || 'Copy code';
          }, 1800);
        };
        try {
          if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            navigator.clipboard.writeText(value).then(function () { done(true); })['catch'](function () { done(legacyCopy(value)); });
          } else {
            done(legacyCopy(value));
          }
        } catch (e) { done(legacyCopy(value)); }
      });
    }
  }

  /* ---------------- events ------------------------------------------------- */
  function reset() {
    root.dataset.resolved = 'false';
    root.removeAttribute('data-match');
    root.removeAttribute('data-need-weight');
    stack.dataset.lplfState = 'off';
    asked = false;
    systems.forEach(function (a) { a.removeAttribute('hidden'); });
    Array.prototype.forEach.call(root.querySelectorAll('[data-lplf-turn]'), function (el) {
      el.innerHTML = '';
    });
    save();
    stamp();
  }

  boxes.forEach(function (b) {
    b.addEventListener('change', function () {
      syncCta();
      publish('finder_selection_changed', { finder_concerns: concerns().join(',') });
      // The verso stays fully live after an answer — it is never dimmed or
      // locked. Toggling a choice re-narrows the recto on the same 200ms, so
      // The result stays live until the explicit Back to answers control.
      if (root.dataset.resolved === 'true') {
        if (checked().length) resolve({ silent: true });
        else reset();
      }
    });
  });

  form.addEventListener('submit', function (event) {
    event.preventDefault();
    if (cta.getAttribute('aria-disabled') === 'true') {
      if (boxes[0]) boxes[0].focus({ preventScroll: true });
      return;
    }
    resolve();
  });

  weights.forEach(function (w) {
    w.addEventListener('change', function () { applyBand(w.dataset.lplfWeight); });
  });

  systems.forEach(function (article) {
    wireEmail(article);
    var discover = article.querySelector('[data-lplf-discover]');
    if (discover) {
      discover.addEventListener('click', function () {
        publish('finder_discovery_clicked', snapshot());
      });
    }
    var shop = article.querySelector('[data-lplf-shop]');
    if (!shop) return;
    shop.addEventListener('click', function (event) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      if (!band()) {
        // Keep the checkout handoff precise: the commerce CTA never escapes
        // to a default tier when a size is still missing.
        event.preventDefault();
        if (!asked) publish('finder_weight_prompted', { finder_match: match() });
        asked = true;
        root.dataset.needWeight = 'true';
        var first = article.querySelector('[data-lplf-weight]');
        if (first) first.focus({ preventScroll: true });
        return;
      }
      publish('finder_shop_clicked', snapshot());
    });
  });

  restart.forEach(function (btn) {
    btn.addEventListener('click', function () {
      // Back to answers preserves the reader's existing ticks.
      reset();
      publish('finder_restarted', {});
      var question = root.querySelector('[data-lplf-question]');
      if (question) {
        question.setAttribute('tabindex', '-1');
        try { question.focus(); } catch (e) { /* ignore */ }
      }
    });
  });

  /* ---------------- boot --------------------------------------------------- */
  syncCta();
  resetDose();

  (function restore() {
    var saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(STORE) || 'null'); } catch (e) { return; }
    if (!saved || !saved.match) return;

    (saved.concerns || []).forEach(function (v) {
      boxes.forEach(function (b) { if (b.value === v) b.checked = true; });
    });
    syncCta();
    if (checked().length) resolve({ silent: true });
    if (saved.weight) applyBand(saved.weight, { silent: true });
    if (saved.sent) {
      root.dataset.sent = 'true';
      syncEmailState();
    }
  })();

  publish('finder_viewed', { finder_source: data.source || 'find-their-system' });
})();
